import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { AgentAvatar } from './AgentAvatars';
import { Send, Play, ArrowLeft, Clock, CheckCircle, XCircle, MessageSquare, ListTodo, FileText, X, Paperclip, Users, Download, PlusCircle, History, Save, Trash2, Sliders, ImageIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// Helper to download AI-generated content as Word or PDF
async function downloadDocument(content, title, format, agentName) {
  try {
    const res = await api.post('/api/documents/generate', { content, title, format, agent_name: agentName });
    const { data, filename, content_type } = res.data;
    const byteChars = atob(data);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: content_type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch (err) { console.error('Download failed:', err); alert('Download failed.'); }
}

export default function AgentWorkspace() {
  const { agentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatFile, setChatFile] = useState(null);
  const chatFileRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [activity, setActivity] = useState([]);
  const [taskHistory, setTaskHistory] = useState([]);
  const [taskModal, setTaskModal] = useState(null);
  const [taskVars, setTaskVars] = useState({});
  const [runningTask, setRunningTask] = useState(false);
  const [taskResult, setTaskResult] = useState(null);
  const [consultModal, setConsultModal] = useState(false);
  const [consultTarget, setConsultTarget] = useState('');
  const [consultQuestion, setConsultQuestion] = useState('');
  const [consulting, setConsulting] = useState(false);
  const [consultResult, setConsultResult] = useState(null);
  const [catalog, setCatalog] = useState([]);
  // Sessions
  const [currentSession, setCurrentSession] = useState('default');
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  // Agent documents
  const [agentDocs, setAgentDocs] = useState([]);
  const [saveDocModal, setSaveDocModal] = useState(null);
  const [docTitle, setDocTitle] = useState('');
  // Guidelines
  const [guidelines, setGuidelines] = useState({});
  const [savingGuidelines, setSavingGuidelines] = useState(false);
  const [guidelinesSuccess, setGuidelinesSuccess] = useState('');
  // Image generation
  const [showImageGen, setShowImageGen] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [imageTitle, setImageTitle] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const chatEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    loadAgent(); loadActivity(); loadTaskHistory(); loadCatalog(); loadSessions(); loadAgentDocs(); loadGuidelines();
    connectWebSocket();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [agentId]);

  useEffect(() => { loadMessages(); }, [agentId, currentSession]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const connectWebSocket = () => {
    if (!user?.company_id) return;
    try {
      const wsUrl = (process.env.REACT_APP_BACKEND_URL || '').replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/ws/${user.company_id}`);
      ws.onmessage = (event) => { setActivity(prev => [JSON.parse(event.data), ...prev].slice(0, 20)); };
      wsRef.current = ws;
    } catch (e) { console.error(e); }
  };

  const loadAgent = async () => { try { const r = await api.get(`/api/agents/${agentId}`); setAgent(r.data); } catch (e) { navigate('/'); } };
  const loadMessages = async () => { try { const r = await api.get(`/api/chat/${agentId}/messages?session_id=${currentSession}`); setMessages(r.data || []); } catch (e) { console.error(e); } };
  const loadActivity = async () => { try { const r = await api.get('/api/activity'); setActivity((r.data || []).filter(e => e.agent_id === agentId || e.agent_id === 'system').slice(0, 20)); } catch (e) { console.error(e); } };
  const loadTaskHistory = async () => { try { const r = await api.get(`/api/tasks/${agentId}/history`); setTaskHistory(r.data || []); } catch (e) { console.error(e); } };
  const loadCatalog = async () => { try { const r = await api.get('/api/agents/catalog'); setCatalog(r.data || []); } catch (e) { console.error(e); } };
  const loadSessions = async () => { try { const r = await api.get(`/api/chat/${agentId}/sessions`); setSessions(r.data || []); } catch (e) { console.error(e); } };
  const loadAgentDocs = async () => { try { const r = await api.get(`/api/agents/${agentId}/documents`); setAgentDocs(r.data || []); } catch (e) { console.error(e); } };
  const loadGuidelines = async () => { try { const r = await api.get(`/api/agents/${agentId}/guidelines`); setGuidelines(r.data || {}); } catch (e) { console.error(e); } };

  const handleSaveGuidelines = async () => {
    setSavingGuidelines(true);
    try {
      await api.patch(`/api/agents/${agentId}/guidelines`, { guidelines });
      setGuidelinesSuccess('Guidelines saved!');
      setTimeout(() => setGuidelinesSuccess(''), 3000);
    } catch (e) { console.error(e); }
    finally { setSavingGuidelines(false); }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return;
    setGeneratingImage(true); setGeneratedImage(null);
    try {
      const res = await api.post('/api/agents/generate-image', {
        prompt: imagePrompt, agent_id: agentId, save_to_docs: true, title: imageTitle || 'AI Generated Image'
      });
      setGeneratedImage(res.data);
      loadAgentDocs();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Image generation failed');
    }
    finally { setGeneratingImage(false); }
  };


  const sendMessage = async () => {
    if ((!input.trim() && !chatFile) || sending) return;
    const msg = input.trim();
    const displayMsg = chatFile ? `${msg}\n[Attached: ${chatFile.name}]` : msg;
    setInput(''); setSending(true);
    setMessages(prev => [...prev, { id: 'temp', role: 'user', content: displayMsg, timestamp: new Date().toISOString() }]);
    try {
      let res;
      if (chatFile) {
        const fd = new FormData(); fd.append('deployed_agent_id', agentId); fd.append('message', msg); fd.append('file', chatFile);
        res = await api.post('/api/chat/send-with-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setChatFile(null); if (chatFileRef.current) chatFileRef.current.value = '';
      } else {
        res = await api.post('/api/chat/send', { message: msg, deployed_agent_id: agentId, session_id: currentSession });
      }
      setMessages(prev => [...prev.filter(m => m.id !== 'temp'), res.data.user_message, res.data.assistant_message]);
      loadAgent();
    } catch (err) {
      setMessages(prev => [...prev.filter(m => m.id !== 'temp'),
        { id: 'err', role: 'user', content: msg, timestamp: new Date().toISOString() },
        { id: 'err-r', role: 'assistant', content: 'Sorry, something went wrong.', timestamp: new Date().toISOString() }
      ]);
    } finally { setSending(false); }
  };

  const handleNewChat = async () => {
    try {
      const res = await api.post(`/api/chat/${agentId}/new-session`, { title: `Chat ${new Date().toLocaleString()}` });
      setCurrentSession(res.data.session_id);
      setMessages([]);
      loadSessions();
    } catch (e) { console.error(e); }
  };

  const handleSaveDoc = async (content) => {
    if (!docTitle.trim()) return;
    try {
      await api.post(`/api/agents/${agentId}/save-document`, { content, title: docTitle });
      setSaveDocModal(null); setDocTitle('');
      loadAgentDocs();
    } catch (e) { console.error(e); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.delete(`/api/agents/documents/${docId}`); loadAgentDocs(); } catch (e) { console.error(e); }
  };

  const handleConsult = async () => {
    if (!consultTarget || !consultQuestion.trim()) return;
    setConsulting(true); setConsultResult(null);
    try {
      const res = await api.post('/api/agents/consult', { source_agent_id: agentId, target_agent_type: consultTarget, question: consultQuestion });
      setConsultResult(res.data); loadActivity();
    } catch (err) { setConsultResult({ response: 'Consultation failed' }); }
    finally { setConsulting(false); }
  };

  const runTask = async (template) => {
    setRunningTask(true); setTaskResult(null);
    try {
      const res = await api.post('/api/tasks/run', { deployed_agent_id: agentId, template_id: template.id, variables: taskVars });
      setTaskResult(res.data); loadTaskHistory(); loadAgent();
    } catch (err) { setTaskResult({ status: 'failed', output: err.response?.data?.detail || 'Task failed' }); }
    finally { setRunningTask(false); }
  };

  const extractVars = (prompt) => [...new Set((prompt.match(/\{(\w+)\}/g) || []).map(m => m.replace(/[{}]/g, '')))];
  const formatTime = (ts) => { if (!ts) return ''; const d = new Date(ts); return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); };

  if (!agent) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in">
      <button onClick={() => navigate('/')} className="btn-secondary" style={{ marginBottom: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }} data-testid="back-to-dashboard">
        <ArrowLeft size={16} />Back to Dashboard
      </button>

      <div className="workspace">
        <div className="workspace-main">
          <div className="ws-header">
            <AgentAvatar agentType={agent.agent_type} size={44} />
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>{agent.agent_name}</h3>
              <p style={{ fontSize: 12, color: agent.color, fontWeight: 600 }}>{agent.agent_title}</p>
            </div>
            <span className={`agent-status ${agent.status}`}><span className={`status-dot ${agent.status}`} />{agent.status}</span>
          </div>

          {/* Tabs */}
          <div className="ws-tabs">
            {[
              { id: 'chat', icon: MessageSquare, label: 'Chat' },
              { id: 'tasks', icon: ListTodo, label: 'Tasks' },
              { id: 'history', icon: History, label: 'History' },
              { id: 'documents', icon: FileText, label: 'Documents' },
              { id: 'guidelines', icon: Sliders, label: 'Guidelines' },
            ].map(t => (
              <div key={t.id} className={`ws-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}>
                <t.icon size={14} style={{ marginRight: 6 }} />{t.label}
                {t.id === 'documents' && agentDocs.length > 0 && <span style={{ marginLeft: 4, fontSize: 10, background: 'var(--panzix-green)', color: 'white', borderRadius: 10, padding: '1px 6px' }}>{agentDocs.length}</span>}
              </div>
            ))}
          </div>

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <>
              {/* Session bar */}
              <div style={{ padding: '8px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafbfc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>Session:</span>
                  <button onClick={() => setShowSessions(!showSessions)} style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }} data-testid="session-selector">
                    {currentSession === 'default' ? 'Initial Chat' : sessions.find(s => s.session_id === currentSession)?.title || currentSession}
                  </button>
                  {showSessions && (
                    <div style={{ position: 'absolute', top: 175, left: 240, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, padding: 8, minWidth: 220 }}>
                      <div onClick={() => { setCurrentSession('default'); setShowSessions(false); }} style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: currentSession === 'default' ? 700 : 400, background: currentSession === 'default' ? '#f0fdf4' : 'transparent' }}>
                        Initial Chat
                      </div>
                      {sessions.filter(s => s.session_id !== 'default').map(s => (
                        <div key={s.session_id} onClick={() => { setCurrentSession(s.session_id); setShowSessions(false); }}
                          style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: currentSession === s.session_id ? 700 : 400, background: currentSession === s.session_id ? '#f0fdf4' : 'transparent' }}>
                          {s.title} <span style={{ fontSize: 10, color: '#94a3b8' }}>({s.message_count} msgs)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={handleNewChat} className="btn-action" style={{ padding: '4px 12px', fontSize: 11 }} data-testid="new-chat-btn">
                  <PlusCircle size={12} style={{ marginRight: 4 }} />New Chat
                </button>
              </div>

              <div className="chat-area" data-testid="chat-area">
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                    <AgentAvatar agentType={agent.agent_type} size={80} />
                    <h4 style={{ marginTop: 16, fontSize: 18, fontWeight: 600, color: '#475569' }}>Start a conversation with {agent.agent_name}</h4>
                    <p style={{ fontSize: 14, maxWidth: 400, margin: '8px auto' }}>{agent.description}</p>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={msg.id || i} className={`chat-message ${msg.role}`}>
                    {msg.role === 'assistant' ? (
                      <>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <button onClick={() => { setSaveDocModal(msg.content); setDocTitle(''); }} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: '#166534', fontWeight: 600 }} data-testid={`save-doc-${i}`}>
                            <Save size={12} />Save as Document
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {msg.content}
                        {msg.has_file && <div style={{ marginTop: 4, fontSize: 11, opacity: 0.7 }}>Attached: {msg.file_name}</div>}
                      </>
                    )}
                    <div className="msg-time">{formatTime(msg.timestamp)}</div>
                  </div>
                ))}
                {sending && <div className="typing-indicator"><div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" /></div>}
                <div ref={chatEndRef} />
              </div>

              <div className="chat-input-area">
                <button onClick={() => chatFileRef.current?.click()} style={{ background: 'none', border: `2px solid ${chatFile ? 'var(--panzix-green)' : '#e2e8f0'}`, borderRadius: 10, padding: '10px', cursor: 'pointer', flexShrink: 0 }} data-testid="attach-file-btn">
                  <Paperclip size={18} color={chatFile ? 'var(--panzix-green)' : '#94a3b8'} />
                </button>
                <input ref={chatFileRef} type="file" accept=".txt,.pdf,.docx,.xlsx,.pptx,.csv,.md,.png,.jpg,.jpeg" style={{ display: 'none' }} onChange={e => setChatFile(e.target.files[0])} />
                <div style={{ flex: 1, position: 'relative' }}>
                  <input data-testid="chat-input" value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder={chatFile ? `Ask about ${chatFile.name}...` : `Ask ${agent.agent_name} anything...`}
                    disabled={sending} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none' }} />
                  {chatFile && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--panzix-green)', fontWeight: 600 }}>{chatFile.name}</span>}
                </div>
                <button onClick={() => setConsultModal(true)} style={{ background: 'none', border: '2px solid #e2e8f0', borderRadius: 10, padding: '10px', cursor: 'pointer', flexShrink: 0 }} data-testid="consult-btn">
                  <Users size={18} color="#7B1FA2" />
                </button>
                <button onClick={sendMessage} disabled={sending || (!input.trim() && !chatFile)} data-testid="send-btn">
                  <Send size={16} />
                </button>
              </div>
            </>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div className="task-list">
              <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Task Templates</h4>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Run pre-built HR tasks powered by {agent.agent_name}.</p>
              {(agent.task_templates || []).map(t => (
                <div key={t.id} className="task-template-card" onClick={() => { setTaskModal(t); setTaskVars({}); setTaskResult(null); }} data-testid={`task-${t.id}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Play size={16} color={agent.color} />
                    <div><h4>{t.name}</h4><p>{t.description}</p></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="task-list">
              <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Task History</h4>
              {taskHistory.length === 0 ? <div className="empty-state"><p>No tasks have been run yet.</p></div> : (
                taskHistory.map(t => (
                  <div key={t.id} className="task-template-card" onClick={() => { setTaskModal({ name: t.task_name }); setTaskResult(t); }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {t.status === 'completed' ? <CheckCircle size={16} color="#22c55e" /> : <XCircle size={16} color="#ef4444" />}
                        <div><h4>{t.task_name}</h4><p>{new Date(t.created_at).toLocaleString()}</p></div>
                      </div>
                      <span className={`agent-status ${t.status === 'completed' ? 'active' : 'paused'}`} style={{ fontSize: 11 }}>{t.status}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="task-list">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Generated Documents & Images</h4>
                  <p style={{ fontSize: 13, color: '#64748b' }}>Documents and images created by {agent.agent_name}.</p>
                </div>
                <button className="btn-action purple" onClick={() => { setShowImageGen(true); setGeneratedImage(null); setImagePrompt(''); setImageTitle(''); }} style={{ fontSize: 12, borderRadius: 10, padding: '8px 16px' }} data-testid="generate-image-btn">
                  <ImageIcon size={14} style={{ marginRight: 6 }} />Generate Image
                </button>
              </div>
              {agentDocs.length === 0 ? (
                <div className="empty-state">
                  <FileText size={64} />
                  <h4>No Documents Yet</h4>
                  <p>Ask {agent.agent_name} to create a document in chat and click "Save as Document", or generate images using the button above.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {agentDocs.map(doc => (
                    <div key={doc.id} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }} data-testid={`agent-doc-${doc.id}`}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {doc.is_image && <ImageIcon size={16} color="#7B1FA2" />}
                          {!doc.is_image && <FileText size={16} color="var(--panzix-green)" />}
                          <div>
                            <h4 style={{ fontSize: 14, fontWeight: 700 }}>{doc.title}</h4>
                            <p style={{ fontSize: 11, color: '#94a3b8' }}>
                              {doc.is_image ? 'Image' : 'Document'} &middot; Created by {doc.agent_name} on {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {!doc.is_image && <>
                            <button onClick={() => downloadDocument(doc.content, doc.title, 'docx', doc.agent_name)} className="btn-action" style={{ padding: '6px 12px', fontSize: 11 }}><Download size={12} style={{ marginRight: 4 }} />Word</button>
                            <button onClick={() => downloadDocument(doc.content, doc.title, 'pdf', doc.agent_name)} className="btn-action purple" style={{ padding: '6px 12px', fontSize: 11 }}><Download size={12} style={{ marginRight: 4 }} />PDF</button>
                          </>}
                          {doc.is_image && doc.image_data && (
                            <a href={doc.image_data} download={`${doc.title}.png`} className="btn-action" style={{ padding: '6px 12px', fontSize: 11, textDecoration: 'none' }}><Download size={12} style={{ marginRight: 4 }} />Download</a>
                          )}
                          <button onClick={() => handleDeleteDoc(doc.id)} className="btn-delete" style={{ padding: '6px 8px', fontSize: 11 }}><Trash2 size={12} /></button>
                        </div>
                      </div>
                      {doc.is_image && doc.image_data ? (
                        <img src={doc.image_data} alt={doc.title} style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 10, marginTop: 8 }} />
                      ) : (
                        <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{doc.content?.slice(0, 200)}...</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Guidelines Tab */}
          {activeTab === 'guidelines' && (
            <div className="task-list" style={{ padding: 24 }}>
              <h4 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>AI Guidelines for {agent.agent_name}</h4>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Customize how {agent.agent_name} communicates and creates content. These guidelines shape every response.</p>
              
              {guidelinesSuccess && <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', background: '#dcfce7', color: '#166534', borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600 }}><CheckCircle size={16} />{guidelinesSuccess}</div>}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Writing Style</label>
                  <select data-testid="guideline-writing-style" value={guidelines.writing_style || 'professional'} onChange={e => setGuidelines(g => ({ ...g, writing_style: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="professional">Professional</option>
                    <option value="casual">Casual</option>
                    <option value="formal">Formal</option>
                    <option value="technical">Technical</option>
                    <option value="academic">Academic</option>
                    <option value="conversational">Conversational</option>
                    <option value="concise">Concise</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Tone</label>
                  <select data-testid="guideline-tone" value={guidelines.tone || 'friendly'} onChange={e => setGuidelines(g => ({ ...g, tone: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="friendly">Friendly</option>
                    <option value="neutral">Neutral</option>
                    <option value="empathetic">Empathetic</option>
                    <option value="authoritative">Authoritative</option>
                    <option value="encouraging">Encouraging</option>
                    <option value="direct">Direct</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Response Length</label>
                  <select data-testid="guideline-length" value={guidelines.response_length || 'detailed'} onChange={e => setGuidelines(g => ({ ...g, response_length: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="brief">Brief (1-2 paragraphs)</option>
                    <option value="moderate">Moderate (3-5 paragraphs)</option>
                    <option value="detailed">Detailed (comprehensive)</option>
                    <option value="bullet_points">Bullet Points</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Formatting</label>
                  <select data-testid="guideline-formatting" value={guidelines.formatting || 'markdown'} onChange={e => setGuidelines(g => ({ ...g, formatting: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="markdown">Markdown (headers, lists, bold)</option>
                    <option value="plain">Plain Text</option>
                    <option value="structured">Structured (sections & tables)</option>
                    <option value="minimal">Minimal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Language</label>
                  <select data-testid="guideline-language" value={guidelines.language || 'english'} onChange={e => setGuidelines(g => ({ ...g, language: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="english">English</option>
                    <option value="spanish">Spanish</option>
                    <option value="french">French</option>
                    <option value="german">German</option>
                    <option value="portuguese">Portuguese</option>
                    <option value="mandarin">Mandarin Chinese</option>
                    <option value="japanese">Japanese</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Image Style</label>
                  <select data-testid="guideline-image-style" value={guidelines.image_style || 'modern'} onChange={e => setGuidelines(g => ({ ...g, image_style: e.target.value }))}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }}>
                    <option value="modern">Modern & Clean</option>
                    <option value="corporate">Corporate</option>
                    <option value="infographic">Infographic</option>
                    <option value="minimalist">Minimalist</option>
                    <option value="colorful">Colorful & Vibrant</option>
                    <option value="hand_drawn">Hand-drawn / Sketch</option>
                    <option value="photorealistic">Photorealistic</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>Industry Terminology / Jargon</label>
                <input data-testid="guideline-terminology" value={guidelines.terminology || ''} onChange={e => setGuidelines(g => ({ ...g, terminology: e.target.value }))}
                  placeholder="e.g., SaaS metrics, healthcare compliance terms, legal terminology..."
                  style={{ fontSize: 13 }} />
              </div>
              <div className="form-group" style={{ marginTop: 4 }}>
                <label style={{ fontWeight: 600, fontSize: 13 }}>Custom Instructions</label>
                <textarea data-testid="guideline-custom" value={guidelines.custom_instructions || ''} onChange={e => setGuidelines(g => ({ ...g, custom_instructions: e.target.value }))}
                  placeholder={`Any specific instructions for ${agent.agent_name}. For example:\n- Always include action items at the end\n- Reference our company values\n- Use our brand voice guidelines\n- Avoid technical jargon when possible`}
                  style={{ width: '100%', minHeight: 100, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none' }} />
              </div>
              <button className="btn-action" onClick={handleSaveGuidelines} disabled={savingGuidelines} style={{ width: '100%', marginTop: 8, fontSize: 14 }} data-testid="save-guidelines">
                <Save size={15} style={{ marginRight: 6 }} />{savingGuidelines ? 'Saving...' : 'Save Guidelines'}
              </button>
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10, textAlign: 'center' }}>Guidelines are applied to all future chat messages and task outputs from {agent.agent_name}.</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="workspace-sidebar">
          <div className="agent-info-panel">
            <h4>Agent Info</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {[['Tasks Done', agent.metrics?.tasks_completed || 0], ['Messages', agent.metrics?.messages_sent || 0], ['Hours Saved', (agent.metrics?.hours_saved || 0).toFixed(1)], ['Accuracy', `${agent.metrics?.accuracy_score || 95}%`]].map(([l, v]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#64748b' }}>{l}</span><span style={{ fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="activity-panel">
            <h4><Clock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />Activity Feed</h4>
            {activity.length === 0 ? <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No activity yet</p> : (
              activity.slice(0, 10).map((evt, i) => (
                <div key={evt.id || i} className="activity-item">
                  <div className="activity-dot" style={{ background: evt.event_type?.includes('completed') ? '#22c55e' : evt.event_type?.includes('failed') ? '#ef4444' : '#3b82f6' }} />
                  <div><div className="activity-text">{evt.message}</div><div className="activity-time">{formatTime(evt.timestamp)}</div></div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Task Modal */}
      {taskModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setTaskModal(null)}>
          <div className="task-modal slide-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>{taskModal.name}</h3>
              <button onClick={() => setTaskModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="close-task-modal"><X size={20} color="#64748b" /></button>
            </div>
            {taskModal.prompt && !taskResult && (
              <>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{taskModal.description}</p>
                {extractVars(taskModal.prompt).map(v => (
                  <div key={v} className="form-group">
                    <label style={{ textTransform: 'capitalize' }}>{v.replace(/_/g, ' ')}</label>
                    <input data-testid={`var-${v}`} value={taskVars[v] || ''} onChange={e => setTaskVars(prev => ({ ...prev, [v]: e.target.value }))} placeholder={`Enter ${v.replace(/_/g, ' ')}...`} />
                  </div>
                ))}
                <button className="btn-action" onClick={() => runTask(taskModal)} disabled={runningTask} data-testid="run-task-btn" style={{ width: '100%' }}>
                  {runningTask ? 'Running...' : <><Play size={16} style={{ marginRight: 6 }} />Run Task</>}
                </button>
              </>
            )}
            {runningTask && <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" style={{ margin: '0 auto 16px', borderTopColor: agent.color }} /><p style={{ fontSize: 14, color: '#64748b' }}>{agent.agent_name} is working on this...</p></div>}
            {taskResult && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  {taskResult.status === 'completed' ? <CheckCircle size={18} color="#22c55e" /> : <XCircle size={18} color="#ef4444" />}
                  <span style={{ fontWeight: 600, color: taskResult.status === 'completed' ? '#166534' : '#991b1b' }}>Task {taskResult.status}</span>
                </div>
                <div className="task-result" data-testid="task-result"><ReactMarkdown>{taskResult.output || 'No output'}</ReactMarkdown></div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn-action" style={{ flex: 1, fontSize: 13 }} onClick={() => downloadDocument(taskResult.output, taskResult.task_name || 'Task_Result', 'docx', agent.agent_name)} data-testid="download-task-docx">
                    <Download size={14} style={{ marginRight: 6 }} />Download Word
                  </button>
                  <button className="btn-action purple" style={{ flex: 1, fontSize: 13 }} onClick={() => downloadDocument(taskResult.output, taskResult.task_name || 'Task_Result', 'pdf', agent.agent_name)} data-testid="download-task-pdf">
                    <Download size={14} style={{ marginRight: 6 }} />Download PDF
                  </button>
                </div>
                <button className="btn-secondary" onClick={() => { setSaveDocModal(taskResult.output); setDocTitle(taskResult.task_name || ''); }} style={{ width: '100%', marginTop: 8, fontSize: 13 }}>
                  <Save size={14} style={{ marginRight: 6 }} />Save to Documents
                </button>
                <button className="btn-secondary" onClick={() => { setTaskResult(null); setTaskVars({}); }} style={{ width: '100%', marginTop: 8 }}>Run Another Task</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save Document Modal */}
      {saveDocModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setSaveDocModal(null)}>
          <div className="task-modal slide-up" style={{ maxWidth: 440 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>Save as Document</h3>
              <button onClick={() => setSaveDocModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div className="form-group">
              <label>Document Title</label>
              <input data-testid="save-doc-title" value={docTitle} onChange={e => setDocTitle(e.target.value)} placeholder="e.g., Onboarding Checklist Q1 2026" />
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Preview: {saveDocModal?.slice(0, 150)}...</p>
            <button className="btn-action" onClick={() => handleSaveDoc(saveDocModal)} disabled={!docTitle.trim()} style={{ width: '100%' }} data-testid="confirm-save-doc">
              <Save size={14} style={{ marginRight: 6 }} />Save Document
            </button>
          </div>
        </div>
      )}

      {/* Consult Modal */}
      {consultModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setConsultModal(false)}>
          <div className="task-modal slide-up">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3>Consult Another AI Employee</h3>
              <button onClick={() => { setConsultModal(false); setConsultResult(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Have {agent.agent_name} consult with another AI employee for cross-functional expertise.</p>
            {!consultResult && (
              <>
                <div className="form-group">
                  <label>Select AI Employee to Consult</label>
                  <select data-testid="consult-target" value={consultTarget} onChange={e => setConsultTarget(e.target.value)} style={{ width: '100%', padding: '12px 16px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14 }}>
                    <option value="">Choose an employee...</option>
                    {catalog.filter(c => c.id !== agent.agent_type && c.available).map(c => <option key={c.id} value={c.id}>{c.name} - {c.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Question / Context</label>
                  <textarea data-testid="consult-question" value={consultQuestion} onChange={e => setConsultQuestion(e.target.value)} placeholder={`What should ${agent.agent_name} ask?`}
                    style={{ width: '100%', minHeight: 100, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} />
                </div>
                <button className="btn-action purple" onClick={handleConsult} disabled={consulting || !consultTarget || !consultQuestion.trim()} style={{ width: '100%' }} data-testid="send-consult">
                  {consulting ? 'Consulting...' : <><Users size={16} style={{ marginRight: 6 }} />Send Consultation</>}
                </button>
              </>
            )}
            {consulting && <div style={{ textAlign: 'center', padding: 24 }}><div className="spinner" style={{ margin: '0 auto 16px', borderTopColor: '#7B1FA2' }} /><p style={{ fontSize: 14, color: '#64748b' }}>Consulting...</p></div>}
            {consultResult && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <AgentAvatar agentType={consultResult.target_agent_type || consultTarget} size={32} />
                  <div><strong>{consultResult.target_agent_name || 'Agent'}</strong><span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>Expert Response</span></div>
                </div>
                <div className="task-result" data-testid="consult-result"><ReactMarkdown>{consultResult.response || 'No response'}</ReactMarkdown></div>
                <button className="btn-secondary" onClick={() => { setConsultResult(null); setConsultQuestion(''); setConsultTarget(''); }} style={{ width: '100%', marginTop: 12 }}>Ask Another Question</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Generation Modal */}
      {showImageGen && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setShowImageGen(false)}>
          <div className="task-modal slide-up" style={{ maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3><ImageIcon size={20} style={{ marginRight: 8, verticalAlign: 'middle', color: '#7B1FA2' }} />Generate Image</h3>
              <button onClick={() => setShowImageGen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              Describe the image you want {agent.agent_name} to create. It will be saved to the Documents tab.
            </p>
            {!generatedImage ? (
              <>
                <div className="form-group">
                  <label>Image Title</label>
                  <input value={imageTitle} onChange={e => setImageTitle(e.target.value)} placeholder="e.g., Company Culture Illustration" data-testid="image-title" />
                </div>
                <div className="form-group">
                  <label>Image Description</label>
                  <textarea value={imagePrompt} onChange={e => setImagePrompt(e.target.value)}
                    placeholder="Describe the image in detail. E.g., 'A modern office with diverse team members collaborating around a whiteboard, bright and professional atmosphere, illustration style'"
                    style={{ width: '100%', minHeight: 100, padding: 12, border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }} data-testid="image-prompt" />
                </div>
                <button className="btn-action purple" onClick={handleGenerateImage} disabled={generatingImage || !imagePrompt.trim()}
                  style={{ width: '100%', borderRadius: 12, fontSize: 14 }} data-testid="generate-image-submit">
                  {generatingImage ? (
                    <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2, display: 'inline-block', marginRight: 8, borderTopColor: 'white' }} />Generating image (this may take up to 60 seconds)...</>
                  ) : (
                    <><ImageIcon size={16} style={{ marginRight: 6 }} />Generate Image</>
                  )}
                </button>
              </>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <CheckCircle size={18} color="#22c55e" />
                  <span style={{ fontWeight: 600, color: '#166534' }}>Image Generated & Saved!</span>
                </div>
                <img src={generatedImage.image} alt={imageTitle} style={{ width: '100%', borderRadius: 12, marginBottom: 12, border: '1px solid #e2e8f0' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <a href={generatedImage.image} download={`${imageTitle || 'image'}.png`} className="btn-action" style={{ flex: 1, textAlign: 'center', textDecoration: 'none', fontSize: 13, borderRadius: 10 }}>
                    <Download size={14} style={{ marginRight: 6 }} />Download Image
                  </a>
                  <button className="btn-secondary" onClick={() => { setGeneratedImage(null); setImagePrompt(''); setImageTitle(''); }} style={{ flex: 1, fontSize: 13, borderRadius: 10 }}>Create Another</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
